import { SESSION_STORAGE_KEY } from "@/lib/constants";
import { sessionRefresher } from "@/auth/session";
import type { components } from "@dolfin/api-public";

export type ExchangeResult =
  | { ok: true; landing: string }
  | { ok: false; status: number; code?: string; detail?: string };

/**
 * Trades a single-use sign-in code (portal handoff, demo magic link) for a fixed-length session and stores it. The
 * caller then hard-reloads to `landing` so AuthProvider rehydrates: a demo goes to /try, or to its books once built.
 */
export async function exchangeCode(code: string): Promise<ExchangeResult> {
  const resp = await fetch("/api/proxy/v1/auth/exchange", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!resp.ok) {
    const problem = await resp.json().catch(() => null);
    return { ok: false, status: resp.status, code: problem?.code, detail: problem?.detail };
  }
  const data: components["schemas"]["AuthResponse"] = await resp.json();
  const session = {
    token: data.token,
    user: {
      userId: data.userId,
      email: data.email,
      organisations: data.organisations,
      clients: data.clients ?? [],
    },
    activeOrganisationId: data.organisations[0]?.organisationId ?? "",
  };
  // These sessions are fixed-length with no refresh cookie of their own. Revoke any cookie left by an earlier sign-in
  // first, or the next refresh would silently swap this session for that user's.
  await sessionRefresher.logout();
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  return { ok: true, landing: await landingFor(data.token, session.activeOrganisationId) };
}

async function landingFor(token: string, organisationId: string): Promise<string> {
  try {
    const resp = await fetch("/api/proxy/v1/me", { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) return "/";
    const me: components["schemas"]["MeResponse"] = await resp.json();
    const org = me.organisations.find((o) => o.id === organisationId);
    if (!org?.isDemo) return "/";
    return org.demoStatus === "Ready" ? "/books" : "/try";
  } catch {
    return "/"; // DemoChrome still routes a demo on from "/".
  }
}
